import { faCreditCard } from '@fortawesome/free-regular-svg-icons';
import {
  faHandshakeAngle,
  faMoneyBill1Wave,
} from '@fortawesome/free-solid-svg-icons';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import uniq from 'lodash/uniq';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useLazyGetFinancialStatisticsQuery,
  useLazyGetFinancialStatisticsBySearchIdQuery,
} from '@app/api/Analytics';
import {
  useCreateAndPayRecordMutation,
  useDeletePaymentMutation,
  useGetClientBalanceMutation,
  useLazyGetRecordReceiptQuery,
  usePayRecordsByClientBalanceMutation,
  usePrepayRecordMutation,
} from '@app/api/ClientPayments';
import {
  useGetLoyaltyCardsByLeadIdV1Mutation,
  useLazyGetLoyaltyCardsBySerialNumberV1Query,
  useCalculateExternalLoyaltyV1Mutation,
} from '@app/api/ExternalLoyalty';
import type { LoyaltyCard } from '@app/api/ExternalLoyalty';
import {
  useLazyGetRecordInfoAsyncQuery,
  usePayWithBonusesMutation,
} from '@app/api/Loyalty';
import type { LoyaltyModelRecordInfo } from '@app/api/Loyalty';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import { useLazyGetPaymentAccountsForSystemQuery } from '@app/api/PaymentAccounts';
import {
  useLazyGetRecordByIdQuery,
  useLazyGetRecordReceiptPdfQuery,
} from '@app/api/Records/api';
import type {
  ModelRecordPayment,
  ModelRecordPaymentLowerCase,
} from '@app/api/Records/types';
import { useLazyGetPaymentMethodsQuery } from '@app/api/SocialIntegrations';
import { PaymentModalTypeEnum } from '@app/components/RecordModal/components/RecordPayment/types';
import type {
  GeneratePaymentTooltipTitle,
  HandleOptionClickProps,
  PaymentButtonConfig,
  SelectedPaymentMethod,
  ServiceTableRow,
  YookassaPaymentLinkParams,
} from '@app/components/RecordModal/components/RecordPayment/types';
import {
  setHasChanges,
  setIsFullyPaid,
  setIsLoading,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import type { HandlePayByYouKassaProps } from '@app/components/RecordModal/types';
import { config } from '@app/config';
import {
  ACQUIRING,
  ACQUIRING_LABEL,
  BANK,
  BANK_LABEL,
  CALENDAR_PATH,
  CASH,
  CASH_LABEL,
  DATE_FORMATS,
  FRONT_WARNING,
  PDF,
  RECORDS_PATH,
  RIGHTS_IS_DELETE_SALE,
  RIGHTS_IS_TOP_CLIENT_BALANCE,
  RIGHTS_SALES_REGISTER,
  SUCCESS,
  WARNING,
} from '@app/constants';
import {
  useAccessRights,
  useUserPrint,
  useNewUserAccessRights,
} from '@app/hooks';
import { useHeader } from '@app/providers';
import { useCalendarAPI } from '@app/providers/CalendarProvider/hooks';
import {
  useCalendarSettingsContext,
  useTeamCalendarContext,
} from '@app/providers/CalendarProvider/useCalendar';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NSearchIdProvider } from '@app/providers/SearchIdProvider/SearchIdProvider.types';
import { handleCatchError } from '@app/services';
import { useGetRecordPaymentsMutation } from '@app/services/rtkQuery/Records/api';
import { useAppDispatch, useAppSelector } from '@app/store';
import { NAccessRights } from '@app/types';
import type {
  ModelRecordServiceDiscount,
  NServices,
  TModelRecordReceipt,
} from '@app/types';
import { capitalizeKeys, createWSConnection, shortName } from '@app/utils';
import {
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

const { Text } = Typography;

export interface ClientBalanceDataInterface {
  ObjectId: number;
  Balance: number;
}

interface RecordClientServiceDiscountSource {
  Id: number;
  Discounts?: ModelRecordServiceDiscount[] | null;
}

const getDiscountsSum = (discounts?: ModelRecordServiceDiscount[] | null) =>
  (discounts || []).reduce((acc, { Discount }) => acc + (Discount ?? 0), 0);

const getRecordServiceDiscountsByClientServiceId = (
  clientServices?: RecordClientServiceDiscountSource[] | null
) => {
  const discountsByClientServiceId = new Map<
    number,
    ModelRecordServiceDiscount[]
  >();

  (clientServices || []).forEach(({ Id, Discounts }) => {
    discountsByClientServiceId.set(Id, Discounts || []);
  });

  return discountsByClientServiceId;
};

const mapRecordReceiptToTableRows = (
  {
    RecordServices,
    RecordProducts,
  }: Pick<TModelRecordReceipt, 'RecordServices' | 'RecordProducts'>,
  discountsByClientServiceId: Map<number, ModelRecordServiceDiscount[]>
): Array<ServiceTableRow> => {
  const serviceRows = (RecordServices || []).map(
    ({
      Id,
      ClientServiceId,
      ClientService,
      Count,
      Price,
      Discount,
      TotalDiscount,
      Cost,
      Paid,
      Discounts,
      IsExcludedFromLoyaltyDiscount,
    }) => {
      const recordDiscounts =
        discountsByClientServiceId.get(ClientServiceId) || [];
      const discounts = recordDiscounts.length
        ? recordDiscounts
        : Discounts || [];
      const totalDiscount = discounts.length
        ? getDiscountsSum(discounts)
        : TotalDiscount || Discount;

      return {
        key: `service-${Id}`,
        service: ClientService || 'Услуга без названия',
        count: Count,
        price: Price,
        discount: totalDiscount,
        cost: Cost,
        paid: Paid,
        total: Cost - Paid,
        discounts,
        isExcludedFromLoyaltyDiscount: IsExcludedFromLoyaltyDiscount,
      };
    }
  );

  const productRows = (RecordProducts || []).map(
    (
      {
        GroupId,
        ProductId,
        Name,
        Count,
        Price,
        Discount,
        Paid,
        Discounts,
        TotalDiscount,
        Cost,
        IsExcludedFromLoyaltyDiscount,
      },
      index
    ) => {
      const productDiscounts = discountsByClientServiceId.get(ProductId) || [];
      const discounts = productDiscounts.length
        ? productDiscounts
        : Discounts || [];
      const totalDiscount = discounts.length
        ? getDiscountsSum(discounts)
        : TotalDiscount || Discount;

      return {
        key: `product-${GroupId ?? index}-${ProductId}`,
        service: Name || 'Товар без названия',
        count: Count,
        price: Price,
        discount: totalDiscount,
        cost: Cost,
        paid: Paid,
        total: Cost - Paid,
        discounts,
        isExcludedFromLoyaltyDiscount: IsExcludedFromLoyaltyDiscount,
      };
    }
  );

  return [...serviceRows, ...productRows];
};

export const useRecordPayment = () => {
  const [paymentModalType, setPaymentModalType] =
    useState<PaymentModalTypeEnum | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [isPrepaymentDialogOpen, setPrepaymentDialogOpen] = useState(false);
  const [receiptId, setReceiptId] = useState<string>('');
  const [useEmailForPayment, setUseEmailForPayment] = useState<boolean>(false);
  const [prepaymentLoader, setPrepaymentLoader] = useState(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const [selectedAmount, setSelectedAmount] = useState<string>('');
  const [isPrintPdfLoading, setIsPrintPdfLoading] = useState(false);
  const [cashPaymentMethods, setCashPaymentMethods] = useState<
    SelectedPaymentMethod[]
  >([]);
  const [bankPaymentMethods, setBankPaymentMethods] = useState<
    SelectedPaymentMethod[]
  >([]);
  const [acquiringPaymentMethods, setAcquiringPaymentMethods] = useState<
    SelectedPaymentMethod[]
  >([]);
  const [selectedCash, setSelectedCash] =
    useState<SelectedPaymentMethod | null>(null);
  const [selectedBank, setSelectedBank] =
    useState<SelectedPaymentMethod | null>(null);
  const [selectedPrepayment, setSelectedPrepayment] =
    useState<SelectedPaymentMethod | null>(null);
  const [servicesTableData, setServicesTableData] = useState<
    Array<ServiceTableRow>
  >([]);
  const [clientBalanceData, setClientBalanceData] =
    useState<ClientBalanceDataInterface>({ ObjectId: 0, Balance: 0 });
  const [payments, setPayments] = useState<ModelRecordPayment[] | null>(null);
  const [acquiringPaymentMethodNames, setAcquiringPaymentMethodNames] =
    useState<Record<number, Record<string, string>>>({});
  const [paymentId, setPaymentId] = useState(0);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState(false);
  const [paymentDate, setPaymentDate] = useState<string>('');
  const [paymentComment, setPaymentComment] = useState<string>('');
  const [isYooKassaModalOpen, setIsYooKassaModalOpen] =
    useState<boolean>(false);
  const [
    isYookassaPaymentWithEmailModalOpen,
    setIsYookassaPaymentWithEmailModalOpen,
  ] = useState<boolean>(false);
  const [yookassaPaymentLinkParams, setYookassaPaymentLinkParams] =
    useState<YookassaPaymentLinkParams | null>(null);
  const [handledYooKassaPaymentId, setHandledYooKassaPaymentId] = useState<
    number | null
  >(null);
  const [loyaltyCards, setLoyaltyCards] = useState<LoyaltyCard[]>([]);
  const [cardBonusesInfo, setCardBonusesInfo] =
    useState<LoyaltyModelRecordInfo | null>(null);
  const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);
  const [yooKassaPaymentMethodCode, setYooKassaPaymentMethodCode] = useState<
    string | null
  >(null);
  const [currentCard, setCurrentCard] = useState<LoyaltyCard | null>(null);
  const [isNeedToCreateYooKassaPayment, setIsNeedToCreateYooKassaPayment] =
    useState<boolean>(false);
  const [isDeletePaymentLoading, setIsDeletePaymentLoading] =
    useState<boolean>(false);

  const { hasUserRight: hasUserRightToTopClientBalance } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_TOP_CLIENT_BALANCE
  );
  const { hasUserRight: hasUserRightToDeleteSale } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_DELETE_SALE
  );
  const { pathname, search } = useLocation();
  const dispatch = useAppDispatch();
  const { database, activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const { getEmployeesData } = useCalendarAPI();
  const {
    record,
    isOpen,
    clientId: _clientId,
    clientAllRecords,
  } = useAppSelector(selectRecordModal);

  const clientId = useMemo(() => {
    return record?.ClientId || _clientId;
  }, [record?.ClientId, _clientId]);

  const { setSelectedEmployees, setAllEmployees, setFinancialStatistics } =
    useTeamCalendarContext();
  const { dateString: legacyCalendarDateString } = useCalendarSettingsContext();
  const { header } = useHeader();

  const [getRecordReceiptPdf] = useLazyGetRecordReceiptPdfQuery();
  const [getRecordById] = useLazyGetRecordByIdQuery();
  const [getPaymentAccountsForSystem] =
    useLazyGetPaymentAccountsForSystemQuery();
  const [getRecordReceipt] = useLazyGetRecordReceiptQuery();
  const [getClientBalance] = useGetClientBalanceMutation();
  const [payRecordsByClientBalance] = usePayRecordsByClientBalanceMutation();

  const [getFinancialStatisticsBySearchId] =
    useLazyGetFinancialStatisticsBySearchIdQuery();
  const [getRecordPayments] = useGetRecordPaymentsMutation();
  const [deletePayment] = useDeletePaymentMutation();
  const [createAndPayRecord] = useCreateAndPayRecordMutation();
  const [prepayRecord] = usePrepayRecordMutation();
  const [getPaymentMethods] = useLazyGetPaymentMethodsQuery();
  const [getLoyaltyCardsByLeadIdV1] = useGetLoyaltyCardsByLeadIdV1Mutation();
  const [getRecordInfo] = useLazyGetRecordInfoAsyncQuery();
  const [getLoyaltyCardsBySerialNumber] =
    useLazyGetLoyaltyCardsBySerialNumberV1Query();
  const [calculateExternalLoyaltyV1] = useCalculateExternalLoyaltyV1Mutation();
  const [payWithBonuses] = usePayWithBonusesMutation();
  const [getFinancialStatistics] = useLazyGetFinancialStatisticsQuery();

  const { organizationAccessRights } = useNewUserAccessRights();

  const recordServiceDiscountsByClientServiceId = useMemo(
    () => getRecordServiceDiscountsByClientServiceId(record?.ClientServices),
    [record?.ClientServices]
  );

  const hasOrganizationRightToLoyalty = useMemo(
    () =>
      !!organizationAccessRights?.find(
        ({ RightType }) => RightType === NAccessRights.RightType.LOYALTY
      )?.Allowed,
    [organizationAccessRights]
  );

  const isCalendar = pathname === CALENDAR_PATH;
  const isRecordsRegistry = pathname === RECORDS_PATH;
  const calendarDateString = useMemo(() => {
    if (!isCalendar) {
      return legacyCalendarDateString;
    }

    const currentSearchParams = new URLSearchParams(search);
    const currentDate = currentSearchParams.get('date');

    return currentDate || dayjs().format('YYYY-MM-DD');
  }, [isCalendar, legacyCalendarDateString, search]);
  const { Balance } = clientBalanceData;
  const { accessToPaymentWithLoyaltyCardBonuses } = config;

  // Оплата с баланса не может превышать баланс клиента (issue/11051).
  // Единый источник правды: и модалка подтверждения, и сам запрос на списание.
  const balancePaymentAmount = useMemo(() => {
    const amount = Number(
      selectedAmount?.replace(/\s/g, '')?.replace(',', '.')
    );

    if (isNaN(amount) || Balance <= 0) {
      return amount;
    }

    return Math.min(amount, Balance);
  }, [selectedAmount, Balance]);

  // Сумма, которую показываем в PaymentModal: для оплаты с баланса — урезанная
  const paymentModalAmount =
    paymentModalType === PaymentModalTypeEnum.BALANCE &&
    !isNaN(balancePaymentAmount)
      ? balancePaymentAmount.toLocaleString('ru-RU')
      : selectedAmount;

  const recordId = useMemo(() => record?.Id, [record?.Id]);
  const excludeRecordIds = useMemo(
    () => record?.OtherRecords?.map(({ Id }) => Id) || [],
    [record?.OtherRecords]
  );

  const getLatestRecordServiceDiscountsByClientServiceId =
    useCallback(async () => {
      if (!recordId) {
        return recordServiceDiscountsByClientServiceId;
      }

      try {
        const recordResponse = await getRecordById({
          recordId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        return getRecordServiceDiscountsByClientServiceId(
          recordResponse.ClientServices
        );
      } catch (_) {
        return recordServiceDiscountsByClientServiceId;
      }
    }, [
      activeBranchId,
      getRecordById,
      recordId,
      recordServiceDiscountsByClientServiceId,
    ]);

  const buildServicesTableData = useCallback(
    (
      recordReceipt: TModelRecordReceipt,
      discountsByClientServiceId: Map<number, ModelRecordServiceDiscount[]>
    ) => mapRecordReceiptToTableRows(recordReceipt, discountsByClientServiceId),
    []
  );

  const hasPayments = useMemo(() => {
    return !!payments && payments.length > 0;
  }, [payments]);

  const openDeletePeymentModal = useCallback((paymentId: number) => {
    setPaymentId(paymentId);
    setIsOpenConfirmationDialog(true);
  }, []);

  const closeDeletePaymentModal = useCallback(() => {
    setPaymentId(0);
    setIsOpenConfirmationDialog(false);
  }, []);

  const fetchPayments = useCallback(async () => {
    if (!isOpen) return;

    if (!recordId) {
      return showNotification(
        'Произошла ошибка при извлечении идентификатора записи',
        FRONT_WARNING
      );
    }
    try {
      const initialPayments = await getRecordPayments({
        ActiveBranchId: activeBranchId || 0,
        recordIds: uniq([
          recordId,
          ...excludeRecordIds,
          ...clientAllRecords.map(({ Id }) => Id),
        ]),
      }).unwrap();

      setPayments(initialPayments);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    isOpen,
    recordId,
    showNotification,
    getRecordPayments,
    activeBranchId,
    clientAllRecords,
    excludeRecordIds,
  ]);

  // Способ оплаты, выбранный бизнесом в ЮKassa, приходит в платеже кодом
  // (us/11174) — подтягиваем человекочитаемые названия по кассам эквайринга,
  // которые встречаются среди платежей записи, и кэшируем по PaymentAccountId.
  const fetchAcquiringPaymentMethodNames = useCallback(async () => {
    if (!activeBranchId) return;

    const accountIdsToFetch = uniq(
      (payments || [])
        .filter(
          ({ PaymentType, PaymentMethod, PaymentAccountId }) =>
            PaymentType === ACQUIRING &&
            PaymentMethod &&
            !acquiringPaymentMethodNames[PaymentAccountId]
        )
        .map(({ PaymentAccountId }) => PaymentAccountId)
    );

    if (!accountIdsToFetch.length) return;

    try {
      const fetchedEntries = await Promise.all(
        accountIdsToFetch.map(async (paymentAccountId) => {
          const methods = await getPaymentMethods({
            ActiveBranchId: activeBranchId,
            paymentAccountId,
          }).unwrap();

          return [
            paymentAccountId,
            Object.fromEntries(
              methods
                .filter(({ Code, Name }) => Code && Name)
                .map(({ Code, Name }) => [Code, Name])
            ),
          ] as const;
        })
      );

      setAcquiringPaymentMethodNames((prev) => ({
        ...prev,
        ...Object.fromEntries(fetchedEntries),
      }));
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    payments,
    activeBranchId,
    getPaymentMethods,
    showNotification,
    acquiringPaymentMethodNames,
  ]);

  const getRecordReceiptHandler = useCallback(
    async (recordId: number) => {
      try {
        const response = await getRecordReceipt({
          ActiveBranchId: activeBranchId,
          recordId,
        }).unwrap();

        return Promise.resolve(response);
      } catch (error) {
        showNotification(error, WARNING);

        return Promise.reject(error);
      }
    },
    [activeBranchId, showNotification, getRecordReceipt]
  );

  const getLoyaltyCardsByLeadIdV1Handler = useCallback(async () => {
    if (!accessToPaymentWithLoyaltyCardBonuses) return;
    if (record?.ClientId) {
      try {
        const response = await getLoyaltyCardsByLeadIdV1({
          byhelp_lead_id: record.ClientId,
          byhelp_client_id: record.ClientId,
        }).unwrap();
        setLoyaltyCards(response.cards);

        return Promise.resolve(response);
      } catch (_) {
        showNotification(
          handleCatchError(
            'Возникла ошибка при получении данных карт лояльности'
          )
        );
      }
    }
  }, [
    accessToPaymentWithLoyaltyCardBonuses,
    record?.ClientId,
    getLoyaltyCardsByLeadIdV1,
    showNotification,
  ]);

  const getRecordInfoHandler = useCallback(async () => {
    if (record?.Id) {
      try {
        const response = await getRecordInfo({
          ActiveBranchId: activeBranchId,
          recordId: record.Id,
        }).unwrap();

        setCardBonusesInfo(response);

        return response;
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    }
  }, [activeBranchId, getRecordInfo, record?.Id, showNotification]);

  const calculateBonusesToEarn = useCallback(
    async (cardId: string) => {
      const filteredServicesTableData = servicesTableData.filter(
        (item) => !item.isExcludedFromLoyaltyDiscount
      );

      if (!filteredServicesTableData.length) {
        return;
      }

      const items = filteredServicesTableData.map((item) => {
        return {
          product_id: String(item.key),
          quantity: item.count,
          price: item.price,
          paid: item.cost,
        };
      });

      try {
        const { bonuses_to_earn } = await calculateExternalLoyaltyV1({
          card_id: cardId,
          items,
        }).unwrap();

        setCardBonusesInfo({
          BonusesToExceed: 0,
          BonusesToEarn: bonuses_to_earn,
          CardId: null,
          CardNumber: null,
          CardType: 'Bonus',
        });
      } catch (_) {
        showNotification(
          handleCatchError('Возникла ошибка при расчёте бонусов')
        );
      }
    },
    [calculateExternalLoyaltyV1, servicesTableData, showNotification]
  );

  const handleSearchCard = useCallback(
    async (serialNumber: string) => {
      try {
        const { cards } = await getLoyaltyCardsBySerialNumber({
          serialNumber: serialNumber.replace(/\s/g, ''),
        }).unwrap();

        setLoyaltyCards((prevCards) =>
          cards[0] ? [...prevCards, cards[0]] : prevCards
        );
        cards[0];
      } catch (_) {
        showNotification(
          handleCatchError('Не удалось получить карту лояльности')
        );
      }
    },
    [getLoyaltyCardsBySerialNumber, showNotification]
  );

  const initialCardSearchHandler = useCallback(async () => {
    try {
      const recordInfo = await getRecordInfoHandler();
      const loyaltyCardsInfo = await getLoyaltyCardsByLeadIdV1Handler();

      if (
        recordInfo?.CardId &&
        !loyaltyCardsInfo?.cards.find(({ id }) => id === recordInfo?.CardId)
      ) {
        handleSearchCard(recordInfo?.CardNumber as string);
      }
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    getLoyaltyCardsByLeadIdV1Handler,
    getRecordInfoHandler,
    handleSearchCard,
    showNotification,
  ]);

  const getPaymentAccountsForSystemHandler = useCallback(async () => {
    try {
      const response = await getPaymentAccountsForSystem({
        ActiveBranchId: activeBranchId,
        branchId: activeBranchId,
        includeDeleted: false,
      }).unwrap();

      return Promise.resolve(response);
    } catch (error) {
      showNotification(error, WARNING);

      return Promise.reject(error);
    }
  }, [activeBranchId, getPaymentAccountsForSystem, showNotification]);

  const getClientBalanceHandler = useCallback(
    async (clientId: number, excludeRecordIds: number[], recordId: number) => {
      try {
        const response = await getClientBalance({
          ActiveBranchId: activeBranchId,
          clientId,
          data: [...excludeRecordIds, recordId],
        }).unwrap();

        return Promise.resolve(response);
      } catch (error) {
        showNotification(error, WARNING);

        return Promise.reject(error);
      }
    },
    [activeBranchId, showNotification, getClientBalance]
  );

  const initTable = useCallback(async () => {
    if (!isOpen) return;
    if (!recordId) {
      return showNotification(
        'Произошла ошибка при извлечении идентификатора записи',
        FRONT_WARNING
      );
    }

    if (!clientId) {
      return showNotification(
        'Произошла ошибка при извлечении идентификатора клиента',
        FRONT_WARNING
      );
    }

    dispatch(setHasChanges(false));
    dispatch(setIsLoading(true));

    try {
      const [
        recordReceipt,
        paymentAccounts,
        clientBalance,
        discountsByClientServiceId,
      ] = await Promise.all([
        getRecordReceiptHandler(recordId),
        getPaymentAccountsForSystemHandler(),
        getClientBalanceHandler(clientId, excludeRecordIds, recordId),
        getLatestRecordServiceDiscountsByClientServiceId(),
      ]);

      const { TotalCost, TotalPaid } = recordReceipt;
      const servicesTableData = buildServicesTableData(
        recordReceipt,
        discountsByClientServiceId
      );

      const { bank, cash, acquiring } = paymentAccounts.reduce(
        (
          acc: Record<'bank' | 'cash' | 'acquiring', SelectedPaymentMethod[]>,
          item: ModelPaymentAccount
        ) => {
          const { PayType } = item;

          const mapping: Record<string, keyof typeof acc> = {
            [BANK]: 'bank',
            [CASH]: 'cash',
            [ACQUIRING]: 'acquiring',
          };

          const key = mapping[PayType as keyof typeof mapping];
          if (!key) return acc;

          acc[key].push({
            ...item,
            PayType: key,
          });

          return acc;
        },
        { bank: [], cash: [], acquiring: [] }
      );

      const { ObjectId, Balance } = clientBalance;
      setTotalPaid(TotalPaid);

      const remainingAmount = TotalCost - TotalPaid;
      if (remainingAmount === 0) {
        dispatch(setIsFullyPaid(true));
      }

      setTotalAmount(remainingAmount);
      setSelectedAmount(remainingAmount.toLocaleString('ru-RU'));

      const selectedCash =
        cash.find(({ IsDefault }) => IsDefault) || cash[0] || null;
      const selectedBank =
        bank.find(({ IsDefault }) => IsDefault) || bank[0] || null;
      const selectedPrepayment =
        cash.find(({ IsDefault }) => IsDefault) || cash[0] || bank[0] || null;

      setSelectedCash(selectedCash);
      setSelectedBank(selectedBank);
      setSelectedPrepayment(selectedPrepayment);
      setBankPaymentMethods(bank);
      setCashPaymentMethods(cash);
      setAcquiringPaymentMethods(acquiring);

      setServicesTableData(servicesTableData);

      setClientBalanceData({ ObjectId, Balance });
    } catch (error) {
      showNotification(error, WARNING);
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [
    isOpen,
    recordId,
    clientId,
    dispatch,
    showNotification,
    getRecordReceiptHandler,
    getPaymentAccountsForSystemHandler,
    getClientBalanceHandler,
    getLatestRecordServiceDiscountsByClientServiceId,
    buildServicesTableData,
    excludeRecordIds,
  ]);

  const startPaymentDeletion = useCallback(async () => {
    try {
      if (!activeBranchId) {
        return showNotification(
          'Произошла ошибка при определении идентификатора филиала',
          FRONT_WARNING
        );
      }
      setIsDeletePaymentLoading(true);

      await deletePayment({
        ActiveBranchId: activeBranchId,
        paymentId,
        returnToBalance: payments?.find(
          ({ PaymentId }) => PaymentId === paymentId
        )?.IsFromBalance,
      }).unwrap();

      setPayments(
        payments?.filter(({ PaymentId }) => PaymentId !== paymentId) || null
      );

      showNotification('Информация о платеже удалена успешно', SUCCESS);

      await getLoyaltyCardsByLeadIdV1Handler();
      await initTable();
    } catch (error) {
      showNotification(error);
    } finally {
      setIsDeletePaymentLoading(false);
      closeDeletePaymentModal();
    }
  }, [
    activeBranchId,
    deletePayment,
    paymentId,
    payments,
    showNotification,
    getLoyaltyCardsByLeadIdV1Handler,
    initTable,
    closeDeletePaymentModal,
  ]);

  const printPdfFile = useCallback(async () => {
    if (!isOpen) return;
    if (!recordId) {
      return showNotification(
        'Произошла ошибка при извлечении идентификатора записи',
        FRONT_WARNING
      );
    }
    setIsPrintPdfLoading(true);

    try {
      const { Name, Data } = await getRecordReceiptPdf({
        recordId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const downloadButton = document.createElement('a');

      downloadButton.href = `data:${PDF};base64,${Data}`;
      downloadButton.download = Name || `receipt_${recordId}.pdf`;
      downloadButton.style.display = 'none';
      document.body.appendChild(downloadButton);
      downloadButton.click();
      document.body.removeChild(downloadButton);
    } catch (error) {
      showNotification(error);
    } finally {
      setIsPrintPdfLoading(false);
    }
  }, [getRecordReceiptPdf, recordId, showNotification, isOpen, activeBranchId]);

  const getPayTypeLabel = (payType: string): string => {
    const labels: Record<string, string> = {
      [CASH]: CASH_LABEL,
      [BANK]: BANK_LABEL,
      [ACQUIRING]: ACQUIRING_LABEL,
    };

    return labels[payType] ?? '';
  };

  const createPaymentOption = useCallback(
    ({ Id, Name, IsDefault, LegalEntity, PayType }: SelectedPaymentMethod) => ({
      key: `${Id}`,
      label: (
        <div className="payment-component prepayment-select-menu">
          {Name && <Text>{Name}</Text>}
          <Text className="additional-info" type="secondary">
            {getPayTypeLabel(PayType)}
          </Text>
          {LegalEntity && (
            <Text className="additional-info" type="secondary">
              {shortName(LegalEntity)}
            </Text>
          )}
        </div>
      ),
      default: IsDefault,
    }),
    []
  );

  const createPrepaymentOption = useCallback(
    ({ Id, Name, PayType, LegalEntity, IsDefault }: SelectedPaymentMethod) => {
      const payTypeLabel = getPayTypeLabel(PayType);

      return {
        key: `${Id}`,
        label: (
          <div className="payment-component prepayment-select-menu">
            {Name && <Text>{Name}</Text>}
            {PayType && (
              <Text className="additional-info" type="secondary">
                {payTypeLabel}
              </Text>
            )}
            {LegalEntity && (
              <Text className="additional-info" type="secondary">
                {shortName(LegalEntity)}
              </Text>
            )}
          </div>
        ),
        default: IsDefault,
      };
    },
    []
  );

  const generateTooltipTitle: GeneratePaymentTooltipTitle = useCallback(
    (selectedPaymentMethod, dataToRender) => {
      if (!selectedPaymentMethod) return <></>;

      const handlePayType = (value: string | number | boolean | null) => {
        if (value === CASH) return CASH_LABEL;
        if (value === BANK) return BANK_LABEL;

        return value;
      };

      return (
        <>
          {dataToRender.map(({ key, label }) => {
            const value = selectedPaymentMethod[key];

            return (
              !!value && (
                <p key={key} className="payment-tooltip-paragraph">
                  {`${label}: ${handlePayType(value)}`}
                </p>
              )
            );
          })}
        </>
      );
    },
    []
  );

  const onChangeAmount = useCallback((value: string) => {
    setSelectedAmount(value);
  }, []);

  const updateFinancialStatistics = useCallback(async () => {
    if (isCalendar && activeBranchId && calendarDateString) {
      try {
        await getEmployeesData({
          date: calendarDateString,
          onSuccess: (employeeDetails) => {
            setSelectedEmployees(employeeDetails);
            setAllEmployees(employeeDetails);
          },
        });

        const financialStatisticsData = await getFinancialStatistics({
          ActiveBranchId: activeBranchId,
          dateS: calendarDateString,
          dateE: calendarDateString,
        }).unwrap();

        setFinancialStatistics(financialStatisticsData);
      } catch (error) {
        showNotification(error, WARNING);
      }

      return;
    }
    if (isRecordsRegistry) {
      try {
        const response = await getFinancialStatisticsBySearchId({
          ActiveBranchId: activeBranchId,
          searchId: header.searchId as NSearchIdProvider.SearchId,
        }).unwrap();
        setFinancialStatistics(response);
      } catch (error) {
        showNotification(error, WARNING);
      }
      return;
    }
  }, [
    isCalendar,
    activeBranchId,
    calendarDateString,
    isRecordsRegistry,
    getEmployeesData,
    getFinancialStatistics,
    setFinancialStatistics,
    setSelectedEmployees,
    setAllEmployees,
    showNotification,
    getFinancialStatisticsBySearchId,
    header.searchId,
  ]);

  const applyLoyaltyCard = useCallback(async () => {
    const filteredServicesTableData = servicesTableData.filter(
      (item) => !item.isExcludedFromLoyaltyDiscount
    );

    if (!filteredServicesTableData.length) {
      return;
    }

    if (!cardBonusesInfo?.CardId && currentCard?.id && record?.Id) {
      try {
        await payWithBonuses({
          ActiveBranchId: activeBranchId,
          recordId: record.Id,
          data: {
            CardId: currentCard.id,
            CardNumber: currentCard.number,
            BonusesToExceed:
              currentCard.type === 'discount' ? currentCard.discount : 0,
            CardType:
              currentCard.type === 'discount' ? 'LoyaltyDiscount' : 'Bonus',
          },
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    }
  }, [
    servicesTableData,
    cardBonusesInfo?.CardId,
    currentCard,
    record?.Id,
    payWithBonuses,
    activeBranchId,
    showNotification,
  ]);

  // Обновление журнала, платежей и карт лояльности после оплаты (issue/11604).
  // Окно оплаты его не ждёт: запрос карт идёт на book-host, и если он не
  // отвечает, окно не закрывалось. Ошибку каждый шаг показывает сам.
  const refreshAfterPayment = useCallback(async () => {
    try {
      await updateFinancialStatistics();
      await fetchPayments();
      await getLoyaltyCardsByLeadIdV1Handler();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    updateFinancialStatistics,
    fetchPayments,
    getLoyaltyCardsByLeadIdV1Handler,
    showNotification,
  ]);

  const payRecordsByClientPaymentHandler = useCallback(
    async (
      accountId: number,
      amount: number,
      clientId: number,
      comment?: string,
      paymentDate?: string
    ) => {
      if (!activeBranchId) {
        showNotification(
          'Произошла ошибка при определении идентификатора филиала',
          FRONT_WARNING
        );
        return;
      }

      if (!record?.Id) {
        showNotification(
          'Произошла ошибка при извлечении идентификатора записи',
          FRONT_WARNING
        );
        return;
      }

      await applyLoyaltyCard();

      try {
        const response = await createAndPayRecord({
          ActiveBranchId: activeBranchId,
          recordId: record.Id,
          accountId,
          amount,
          paymentDate: paymentDate ?? '',
          comment: comment ?? '',
        }).unwrap();

        const { TotalCost, TotalPaid } = response;
        const discountsByClientServiceId =
          await getLatestRecordServiceDiscountsByClientServiceId();
        const servicesTableData = buildServicesTableData(
          response,
          discountsByClientServiceId
        );
        setTotalPaid(TotalPaid);
        const remainingAmount = TotalCost - TotalPaid;

        if (remainingAmount === 0) {
          dispatch(setIsFullyPaid(true));
        }

        setTotalAmount(remainingAmount);
        setSelectedAmount(remainingAmount.toLocaleString('ru-RU'));
        setServicesTableData(servicesTableData);

        showNotification('Оплата успешно проведена', 'success');
      } catch (error) {
        showNotification(error, WARNING);
        return Promise.reject(error);
      } finally {
        setIsPaymentModalOpen(false);
        setPrepaymentDialogOpen(false);
      }

      void refreshAfterPayment();
    },
    [
      activeBranchId,
      record?.Id,
      applyLoyaltyCard,
      showNotification,
      createAndPayRecord,
      refreshAfterPayment,
      dispatch,
      getLatestRecordServiceDiscountsByClientServiceId,
      buildServicesTableData,
    ]
  );

  const payRecordsByClientBalanceHandler = useCallback(
    async (amount: number) => {
      if (!clientId) {
        return;
      }

      await applyLoyaltyCard();

      try {
        const recordReceipt = await payRecordsByClientBalance({
          ActiveBranchId: activeBranchId,
          data: {
            RecordIds: [record?.Id as number, ...excludeRecordIds],
            Amount: amount,
          },
        }).unwrap();

        const { TotalCost, TotalPaid } = recordReceipt;
        const discountsByClientServiceId =
          await getLatestRecordServiceDiscountsByClientServiceId();
        const servicesTableData = buildServicesTableData(
          recordReceipt,
          discountsByClientServiceId
        );
        setTotalPaid(TotalPaid);
        const remainingAmount = TotalCost - TotalPaid;

        if (remainingAmount === 0) {
          dispatch(setIsFullyPaid(true));
        }

        setTotalAmount(remainingAmount);
        setSelectedAmount(remainingAmount.toLocaleString('ru-RU'));
        setServicesTableData(servicesTableData);

        const { Balance, ObjectId } = await getClientBalance({
          ActiveBranchId: activeBranchId,
          clientId,
          data: [record?.Id as number, ...excludeRecordIds],
        }).unwrap();

        setClientBalanceData({ Balance, ObjectId });

        updateFinancialStatistics();
        fetchPayments();

        showNotification('Оплата успешно проведена', 'success');
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        setIsPaymentModalOpen(false);
        setPrepaymentDialogOpen(false);
      }
    },
    [
      clientId,
      activeBranchId,
      record?.Id,
      excludeRecordIds,
      applyLoyaltyCard,
      updateFinancialStatistics,
      fetchPayments,
      showNotification,
      dispatch,
      getLatestRecordServiceDiscountsByClientServiceId,
      buildServicesTableData,
      payRecordsByClientBalance,
      getClientBalance,
    ]
  );

  const handlePaymentModalOk = useCallback(
    async (accountId: string) => {
      const amount = Number(
        selectedAmount?.replace(/\s/g, '')?.replace(',', '.')
      );

      if (!clientId) {
        return showNotification(
          'Произошла ошибка при извлечении идентификатора клиента',
          FRONT_WARNING
        );
      }

      if (isNaN(amount)) {
        return showNotification(
          'Произошла ошибка при извлечении суммы платежа',
          FRONT_WARNING
        );
      }

      switch (paymentModalType) {
        case PaymentModalTypeEnum.BALANCE:
          await payRecordsByClientBalanceHandler(balancePaymentAmount);
          break;
        default:
          await payRecordsByClientPaymentHandler(
            +accountId,
            amount,
            clientId,
            paymentComment,
            paymentDate
          );
          break;
      }
    },
    [
      balancePaymentAmount,
      clientId,
      payRecordsByClientBalanceHandler,
      payRecordsByClientPaymentHandler,
      paymentComment,
      paymentDate,
      paymentModalType,
      selectedAmount,
      showNotification,
    ]
  );

  const prepayRecordHandler = async (
    args: Omit<NServices.ArgsToPrepayRecord, 'activeBranchId'>
  ) => {
    setPrepaymentLoader(true);
    try {
      const selectedAccount = combinedPaymentMethods.find(
        ({ Id }) => Id === args.accountId
      );

      let paymentMethod: string | undefined;
      // Метод оплаты нужен только для эквайринговых касс (интеграция с
      // ЮKassa) — подтягиваем дефолтный способ, настроенный в интеграции
      // (us/11180). Для остальных типов касс параметр не имеет смысла.
      if (selectedAccount?.PayType === ACQUIRING) {
        try {
          const paymentMethods = await getPaymentMethods({
            ActiveBranchId: activeBranchId,
            paymentAccountId: selectedAccount.Id,
          }).unwrap();

          paymentMethod =
            paymentMethods.find(({ IsDefault }) => IsDefault)?.Code ??
            undefined;
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }

      const recordReceipt = await prepayRecord({
        ...args,
        paymentMethod,
        ActiveBranchId: activeBranchId,
        amount: Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.')),
      }).unwrap();

      const { Balance, TotalCost, TotalPaid } = recordReceipt;
      const discountsByClientServiceId =
        await getLatestRecordServiceDiscountsByClientServiceId();
      const servicesTableData = buildServicesTableData(
        recordReceipt,
        discountsByClientServiceId
      );
      setTotalPaid(TotalPaid);
      const remainingAmount = TotalCost - TotalPaid;

      if (remainingAmount === 0) {
        dispatch(setIsFullyPaid(true));
      }

      setTotalAmount(remainingAmount);
      setSelectedAmount(remainingAmount.toLocaleString('ru-RU'));
      setServicesTableData(servicesTableData);
      setClientBalanceData(Balance);

      updateFinancialStatistics();
      fetchPayments();

      setPrepaymentDialogOpen(false);

      showNotification('Предоплата успешно внесена на баланс клиента', SUCCESS);
    } catch (error) {
      showNotification(error, WARNING);
    } finally {
      setPrepaymentLoader(false);
    }
  };

  const handleOptionClick = useCallback(
    (modalType: PaymentModalTypeEnum) =>
      ({
        receipt,
        yooKassaPaymentMethodCode,
      }: HandleOptionClickProps): void => {
        if (receipt) {
          setReceiptId(String(receipt.Id));
          setUseEmailForPayment(receipt.UseEmail ?? false);
        }

        if (yooKassaPaymentMethodCode) {
          setYooKassaPaymentMethodCode(yooKassaPaymentMethodCode);
        }

        const setOpen =
          modalType === PaymentModalTypeEnum.PREPAYMENT
            ? setPrepaymentDialogOpen
            : setIsPaymentModalOpen;

        setOpen(true);
        setPaymentModalType(modalType);
      },
    []
  );

  const onMenuClickCash = useCallback(
    (key: string) => {
      const selectedCash = cashPaymentMethods.find(({ Id }) => Id === +key);

      setSelectedCash(selectedCash ?? null);
    },
    [cashPaymentMethods, setSelectedCash]
  );

  const onMenuClickBank = useCallback(
    (key: string) => {
      const selectedBank = bankPaymentMethods.find(({ Id }) => Id === +key);

      setSelectedBank(selectedBank ?? null);
    },
    [bankPaymentMethods, setSelectedBank]
  );

  const optionsBank = useMemo(
    () => bankPaymentMethods.map(createPaymentOption),
    [bankPaymentMethods, createPaymentOption]
  );

  const optionsCash = useMemo(
    () => cashPaymentMethods.map(createPaymentOption),
    [cashPaymentMethods, createPaymentOption]
  );

  const combinedPaymentMethods = useMemo(
    () => [
      ...cashPaymentMethods,
      ...bankPaymentMethods,
      ...acquiringPaymentMethods,
    ],
    [cashPaymentMethods, bankPaymentMethods, acquiringPaymentMethods]
  );

  const prepaymentOptions = useMemo(
    () => combinedPaymentMethods.map(createPrepaymentOption),
    [combinedPaymentMethods, createPrepaymentOption]
  );

  const isPrepaymentButtonDisabled = useMemo(() => {
    if (!hasUserRightToTopClientBalance) {
      return true;
    }

    return Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.')) <= 0;
  }, [hasUserRightToTopClientBalance, selectedAmount]);

  const isPayFormBalanceButtonDisabled = useMemo(() => {
    if (Balance <= 0 || !totalAmount) return true;

    return Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.')) <= 0;
  }, [Balance, selectedAmount, totalAmount]);

  const disabledCashButton = useMemo(() => {
    return (
      !totalAmount ||
      optionsCash.length === 0 ||
      !Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.'))
    );
  }, [optionsCash, selectedAmount, totalAmount]);

  const isPayFormYooKassaButtonDisabled = useMemo(() => {
    return (
      !totalAmount ||
      !Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.'))
    );
  }, [selectedAmount, totalAmount]);

  const onMenuClickPrepayment = useCallback(
    (key: string) => {
      const handledSelectedPrepayment = combinedPaymentMethods.find(
        ({ Id }) => Id === +key
      );

      setSelectedPrepayment(handledSelectedPrepayment ?? null);
    },
    [combinedPaymentMethods, setSelectedPrepayment]
  );

  const paymentButtonsOptions: Array<PaymentButtonConfig> = useMemo(
    () => [
      {
        key: 'payment-in-cash-button',
        disabled: disabledCashButton,
        overlayClassName: 'receipt-name',
        className: 'payment-button',
        options: optionsCash,
        handleOptionClick: (receipt?: SelectedPaymentMethod | null) =>
          handleOptionClick(PaymentModalTypeEnum.CASH)({
            receipt: receipt || undefined,
          }),
        tooltipTitle: generateTooltipTitle(selectedCash, [
          { key: 'Name', label: 'Касса' },
          { key: 'Bank', label: 'Банк' },
          { key: 'Description', label: 'Описание' },
        ]),
        icon: faMoneyBill1Wave,
        iconClassName: 'buttons__cash_icon',
        title: 'Оплата наличными',
        description: selectedCash?.Name || '',
        onSelectMenuItem: onMenuClickCash,
        selectedPaymentMethod: selectedCash,
        testId: 'cash-payment',
        dropdownTestId: 'dropdown-arrow-cash',
      },
      {
        key: 'payment-from-bank-button',
        disabled:
          !totalAmount ||
          optionsBank.length === 0 ||
          !Number(selectedAmount?.replace(/\s/g, '')?.replace(',', '.')),
        overlayClassName: 'receipt-name',
        className: 'payment-button',
        options: optionsBank,
        handleOptionClick: (receipt?: SelectedPaymentMethod | null) =>
          handleOptionClick(PaymentModalTypeEnum.CARD)({
            receipt: receipt || undefined,
          }),
        tooltipTitle: generateTooltipTitle(selectedBank, [
          { key: 'Name', label: 'Название кассы' },
          { key: 'Bank', label: 'Банк' },
          { key: 'Description', label: 'Описание' },
        ]),
        icon: faCreditCard,
        iconClassName: 'buttons__card_icon',
        title: 'Оплатить картой',
        description: selectedBank?.Name || '',
        onSelectMenuItem: onMenuClickBank,
        selectedPaymentMethod: selectedBank,
        testId: 'card-payment',
        dropdownTestId: 'dropdown-arrow-card',
      },
      {
        key: 'prepayment-button',
        disabled: isPrepaymentButtonDisabled,
        overlayClassName: 'receipt-name',
        className: 'payment-button',
        options: prepaymentOptions,
        handleOptionClick: (receipt?: SelectedPaymentMethod | null) =>
          handleOptionClick(PaymentModalTypeEnum.PREPAYMENT)({
            receipt: receipt || undefined,
          }),
        tooltipTitle: generateTooltipTitle(selectedPrepayment, [
          { key: 'Name', label: 'Касса' },
          { key: 'PayType', label: 'Метод оплаты' },
          { key: 'LegalEntity', label: 'ЮЛ' },
          { key: 'Description', label: 'Описание' },
        ]),
        icon: faHandshakeAngle,
        iconClassName: 'buttons__prepayment_icon',
        title: 'Внести предоплату',
        description: selectedPrepayment?.Name || '',
        onSelectMenuItem: onMenuClickPrepayment,
        selectedPaymentMethod: selectedPrepayment,
        testId: 'prepayment',
        dropdownTestId: 'dropdown-arrow-prepayment',
      },
    ],
    [
      disabledCashButton,
      handleOptionClick,
      onMenuClickCash,
      optionsBank,
      optionsCash,
      selectedCash,
      totalAmount,
      selectedAmount,
      selectedBank,
      onMenuClickBank,
      isPrepaymentButtonDisabled,
      prepaymentOptions,
      selectedPrepayment,
      onMenuClickPrepayment,
      generateTooltipTitle,
    ]
  );

  const handleYooKassaPaymentModalOpen = useCallback((id: number | null) => {
    setHandledYooKassaPaymentId(id);
    setIsYooKassaModalOpen(true);
  }, []);

  const subscribeToAcquiringPaymentUpdateWsEvent = useCallback(() => {
    if (!database || !clientId) {
      return () => {};
    }

    void createWSConnection(async () => {
      await invokeWSMethod('ListenClient', database, clientId);
      const acquiringHandler = ({
        data,
      }: {
        data: ModelRecordPaymentLowerCase;
      }) => {
        const capitalizedKeysDataObject: ModelRecordPayment =
          capitalizeKeys(data);

        setPayments((prev) =>
          (prev || []).map((item) =>
            item.PaymentId === capitalizedKeysDataObject.PaymentId
              ? capitalizedKeysDataObject
              : item
          )
        );
      };

      const recordPaymentHandler = () => {
        void initTable();
        void fetchPayments();
        void updateFinancialStatistics();
      };

      subscribeToWsEvent('OnAcquiringPaymentUpdate', acquiringHandler);
      subscribeToWsEvent('OnRecordPayment', recordPaymentHandler);
    });

    return () => {
      unsubscribeFromWsEvent('OnAcquiringPaymentUpdate');
      unsubscribeFromWsEvent('OnRecordPayment');
    };
  }, [database, clientId, initTable, fetchPayments, updateFinancialStatistics]);

  const handlePaymentInfoModalOpen = useCallback((paymentId: number | null) => {
    setObjectId(paymentId);
    setPaymentInfoModalOpen(true);
  }, []);

  const cancelPaymentModal = () => {
    setIsPaymentModalOpen(false);
    setUseEmailForPayment(false);
  };

  const handlePayByYouKassa = ({
    Amount,
    PaymentMethod,
    IsNeedToCreatePayment,
    IsNeedToCreateLink,
    ClientPaymentId,
    PaymentAccountId,
  }: HandlePayByYouKassaProps) => {
    setIsNeedToCreateYooKassaPayment(IsNeedToCreatePayment);
    if (useEmailForPayment || IsNeedToCreateLink) {
      // Без клиента и записи платёж в ЮKassa не создать — запрос не отправляем.
      if (!clientId || !recordId) {
        showNotification(
          'Произошла ошибка при извлечении идентификатора записи',
          FRONT_WARNING
        );

        return;
      }

      // Ссылку больше не собираем сами: бэк отдаёт её в ConfirmationUrl с
      // одноразовым токеном, по которому клиент вводит email (us/10744).
      setYookassaPaymentLinkParams({
        clientId,
        recordId,
        amount: Amount,
        paymentDate: dayjs(paymentDate).format(
          DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
        ),
        comment: paymentComment,
        paymentMethod: PaymentMethod,
        paymentAccountId: Number(PaymentAccountId || receiptId),
        clientPaymentsId: ClientPaymentId ?? null,
      });
      setIsYookassaPaymentWithEmailModalOpen(true);
      cancelPaymentModal();
      return;
    }

    handleYooKassaPaymentModalOpen(ClientPaymentId ?? null);
    cancelPaymentModal();
  };

  return {
    servicesTableData,
    initTable,
    printPdfFile,
    isPrintPdfLoading,
    totalAmount,
    onChangeAmount,
    selectedAmount,
    paymentModalAmount,
    paymentButtonsOptions,
    isPayFormBalanceButtonDisabled,
    handleOptionClick,
    Balance,
    paymentModalType,
    isPaymentModalOpen,
    isPrepaymentDialogOpen,
    setPrepaymentDialogOpen,
    receiptId,
    setPrepaymentLoader,
    prepaymentLoader,
    clientBalanceData,
    record,
    handlePaymentModalOk,
    prepayRecordHandler,
    totalPaid,
    payments,
    acquiringPaymentMethodNames,
    fetchAcquiringPaymentMethodNames,
    isDeletePaymentLoading,
    hasPayments,
    openDeletePeymentModal,
    fetchPayments,
    startPaymentDeletion,
    isOpenConfirmationDialog,
    hasUserRightToDeleteSale,
    closeDeletePaymentModal,
    clientId,
    recordId,
    paymentDate,
    setPaymentDate,
    paymentComment,
    setPaymentComment,
    setIsYooKassaModalOpen,
    isYooKassaModalOpen,
    handledYooKassaPaymentId,
    subscribeToAcquiringPaymentUpdateWsEvent,
    acquiringPaymentMethods,
    loyaltyCards,
    cardBonusesInfo,
    getRecordInfoHandler,
    setLoyaltyCards,
    handlePaymentInfoModalOpen,
    paymentInfoModalOpen,
    setPaymentInfoModalOpen,
    objectId,
    setObjectId,
    accessToPaymentWithLoyaltyCardBonuses,
    isPayFormYooKassaButtonDisabled,
    initialCardSearchHandler,
    yooKassaPaymentMethodCode,
    cancelPaymentModal,
    hasOrganizationRightToLoyalty,
    calculateBonusesToEarn,
    setCurrentCard,
    isYookassaPaymentWithEmailModalOpen,
    setIsYookassaPaymentWithEmailModalOpen,
    yookassaPaymentLinkParams,
    setYookassaPaymentLinkParams,
    handlePayByYouKassa,
    isNeedToCreateYooKassaPayment,
    setIsNeedToCreateYooKassaPayment,
    setUseEmailForPayment,
  };
};
